import React, {useEffect, useRef, useState, useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from 'modules';
import {addChat, removeChat, clearChat} from 'modules/chat';
import {isChatAction} from 'modules/ui';
import {getHeight, convertSecondToTime} from 'lib/utils';
import {IVodInfoData} from 'lib/api/vodInfo';
import {CHAT_MAX_LINE} from 'constants/config';
import Chat from 'components/player/chat/Chat';
import {changeSeekChatAction} from 'modules/file';
import emoticonSDK from '@soopkr-fe/emoticon-sdk';

interface IFilesObj {
  vod_url: string;
  duration: number;
  file_info_key: string;
}

/**
 * 채팅 컨테이너
 */
const ChatContainer = () => {
  const dispatch = useDispatch();
  const {vodData, playingTime, currentMediaTime, file, chats} = useSelector(
    (state: RootState) => ({
      vodData: state.vodInfo.titleFileInfo.data,
      playingTime: state.video.playingTime,
      currentMediaTime: state.video.currentMediaTime,
      file: state.file,
      chats: state.chat,
    }),
  );
  const chatAreaRef = useRef<HTMLDivElement>(null); // div.chat_area
  const chatMemoRef = useRef<HTMLDivElement>(null); // div.chat_memoyo
  const [chatScroll, setChatScroll] = useState({
    downBtn: '',
    lock: false,
  }); // 스크롤 상태
  const [startTime, setStatTime] = useState(''); // 채팅 시작 시간 상태

  // 채팅 처음 호출되면 채팅 객체에다 한번만 setItems함
  useEffect(() => {
    const {response} = vodData as IVodInfoData; // ts-optchain 사용법을 알야아할듯...
    if (response.files) {
      const files = response.files.map((obj: IFilesObj) => {
        const rObj = {
          url: obj.vod_url,
          duration: obj.duration,
          fileInfoKey: obj.file_info_key,
        };
        return rObj;
      });
      if (typeof oChatCore !== 'undefined') {
        let chatOption = {};
        let chatUrl = '',
          bjId = '',
          chatDuration = 0;
        let subscription_personalcon: any = [];
        if (response.info) {
          chatUrl = response.info.chat_url; // API로부터 받은 채팅 URL 셋팅
          bjId = response.info.user_id; // BJ_ID
          subscription_personalcon =
            response.info.subscription_personalcon || []; // 구독퍼스나콘
          chatDuration = response.info.chat_duration; // 로드할 채팅 길이(초)
        }
        chatOption = {
          bjId,
          chatUrl,
          subscription_personalcon,
          chatDuration,
          emoticonSDK,
        };
        oChatCore.setChatConfigOption({
          chatOption,
        }); // 채팅 옵션 설정

        oChatCore.setItems(files);

        // 시그니처 이모티콘 load & set
        emoticonSDK.emoticons.signature
          .load({szBjId: response.info.user_id})
          .then((response) => {
            oChatCore.setDirectSignatureEmoticonData(response);
          });
      }
    }
  }, [vodData]);

  useEffect(() => {
    if (file.isChatSeek) {
      dispatch(clearChat());
      setStatTime(convertSecondToTime(playingTime));

      try {
        oChatCore.seek(
          file.list[file.playingIdx].file_info_key,
          currentMediaTime,
        ); // 현재 파일의 미디어 재생시간 넘김=currentMediaTime (전체에서의 재생시간 아님)
      } catch {
        console.log('@@@@@@@@@ ChatCore error');
      }
      dispatch(changeSeekChatAction(false));
    }

    let getChats: Array<string> = [''];
    if (oChatCore.chatItemList && oChatCore.chatItemList.length > 0) {
      try {
        getChats = oChatCore.getChat(
          file.list[file.playingIdx].file_info_key,
          currentMediaTime,
        ); // 현재 파일의 미디어 재생시간 넘김=currentMediaTime (전체에서의 재생시간 아님)
      } catch {
        console.log('@@@@@@@@@ ChatCore error');
      }

      if (getChats !== undefined && getChats.length !== 0) {
        dispatch(addChat(getChats.join('')));

        // 채팅 갯수 MAX_LINE 넘으면 제거
        const chatLen = document.getElementsByClassName('chatData').length;
        if (chatLen > CHAT_MAX_LINE) {
          dispatch(removeChat());
        }
      }
    }
  }, [currentMediaTime]);

  /**
   * 채팅 스크롤 하단으로 계속~
   */
  useEffect(() => {
    if (chatAreaRef.current) {
      if (!chatScroll.lock) {
        chatAreaRef.current.scrollTop = chatAreaRef.current.scrollHeight;
      }
    }
  }, [chats]);

  /**
   * 채팅 영역 마우스 스크롤 이벤트 시 함수
   */
  const onChatScroll = useCallback(() => {
    if (chatAreaRef.current && chatMemoRef.current) {
      const t1 = Number(chatAreaRef.current.scrollTop);
      const t2 = Number(getHeight(chatAreaRef.current));
      const t3 = Number(getHeight(chatMemoRef.current));
      const isLock = t3 <= t1 + t2 + 48 ? false : true;

      if (isLock) {
        setChatScroll({
          downBtn: 'on',
          lock: isLock,
        });
      } else {
        setChatScroll({
          downBtn: '',
          lock: isLock,
        });
      }
    }
  }, [setChatScroll]);
  useEffect(() => {
    if (chatAreaRef.current) {
      chatAreaRef.current.addEventListener('scroll', onChatScroll as any);
    }
    return () => {
      if (chatAreaRef.current) {
        chatAreaRef.current.removeEventListener('scroll', onChatScroll as any);
      }
    };
  }, [onChatScroll]);

  /**
   * 스크롤 다운 버튼 클릭 이벤트 함수
   */
  const onScrollDown = () => {
    if (chatAreaRef.current) {
      chatAreaRef.current.scrollTop = chatAreaRef.current.scrollHeight;
      setChatScroll({
        downBtn: '',
        lock: false,
      });
    }
  };

  /**
   * 채팅 숨기기 버튼 클릭
   */
  const onClickHideChat = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    e.preventDefault();

    dispatch(isChatAction(false));
  };

  return (
    <Chat
      seekTime={startTime}
      chats={chats}
      chatAreaRef={chatAreaRef}
      chatMemoRef={chatMemoRef}
      downBtn={chatScroll.downBtn}
      onScrollDown={onScrollDown}
      onClickHideChat={onClickHideChat}
    />
  );
};

export default ChatContainer;
